/**
 * SearchStates - Combined Loading, Empty, and Error States
 * Mobile-first with skeleton animations
 */

import React, { memo } from 'react';
import { RefreshCw, Search, MapPin, FilterX } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ViewMode } from '@/types/search';

// ============================================
// SKELETON CARD
// ============================================

interface SkeletonCardProps {
  variant: ViewMode;
}

const SkeletonCard = memo(function SkeletonCard({ variant }: SkeletonCardProps) {
  if (variant === 'grid') {
    return (
      <div className="bg-white rounded-xl overflow-hidden border border-black/[0.06] animate-pulse">
        {/* Image Skeleton */}
        <div className="w-full aspect-[16/10] bg-[#F0F0ED]" />
        
        {/* Content Skeleton */}
        <div className="p-4 space-y-3">
          <div className="h-5 bg-[#F0F0ED] rounded-md w-3/4" />
          <div className="h-4 bg-[#F0F0ED] rounded-md w-1/2" />
          <div className="flex items-center gap-2 pt-3 border-t border-black/[0.06]">
            <div className="h-6 w-14 bg-[#F0F0ED] rounded-md" />
            <div className="flex-1" />
            <div className="h-4 w-16 bg-[#F0F0ED] rounded-md" />
          </div>
        </div>
      </div>
    );
  }
  
  // Mobile/List Skeleton
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-black/[0.06] p-3 h-[116px] flex gap-3 animate-pulse">
      {/* Image Skeleton */}
      <div className="w-[88px] h-[88px] rounded-lg bg-[#F0F0ED] flex-shrink-0" />
      
      {/* Content Skeleton */}
      <div className="flex-1 flex flex-col justify-between py-0.5">
        <div className="h-5 bg-[#F0F0ED] rounded-md w-3/4" />
        <div className="h-4 bg-[#F0F0ED] rounded-md w-1/2" />
        <div className="flex items-center gap-2">
          <div className="h-6 w-12 bg-[#F0F0ED] rounded-md" />
          <div className="h-6 w-20 bg-[#F0F0ED] rounded-md" />
          <div className="flex-1" />
          <div className="h-6 w-14 bg-[#F0F0ED] rounded-md" />
        </div>
      </div>
    </div>
  );
});

// ============================================
// LOADING STATE
// ============================================

interface LoadingStateProps {
  viewMode: ViewMode;
  itemCount?: number;
}

export const LoadingState = memo(function LoadingState({ 
  viewMode, 
  itemCount = 6 
}: LoadingStateProps) {
  const items = Array.from({ length: itemCount }, (_, i) => i);
  
  return (
    <div 
      className={cn(
        viewMode === 'grid' 
          ? "grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          : "space-y-3"
      )}
      role="status"
      aria-label="Restaurants werden geladen"
    >
      {items.map((i) => (
        <SkeletonCard key={i} variant={viewMode} />
      ))}
      <span className="sr-only">Lade Restaurants...</span>
    </div>
  );
});

// ============================================
// EMPTY STATE
// ============================================

interface EmptyStateProps {
  title?: string;
  description?: string;
  suggestions?: string[];
  onResetFilters?: () => void;
  onChangeLocation?: () => void;
}

export const EmptyState = memo(function EmptyState({
  title = "Keine Restaurants gefunden",
  description = "Mit den aktuellen Filtern haben wir nichts Passendes gefunden.",
  suggestions = [
    'Filter zurücksetzen',
    'Größeren Umkreis wählen',
    'Anderen Standort eingeben'
  ],
  onResetFilters,
  onChangeLocation
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      {/* Icon */}
      <div className="w-20 h-20 rounded-2xl bg-[#F7F7F5] flex items-center justify-center mb-6">
        <Search className="w-10 h-10 text-[#CCCCCC]" />
      </div>
      
      {/* Title */}
      <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">
        {title}
      </h3>
      
      {/* Description */}
      <p className="text-[#666666] text-sm max-w-sm mb-6">
        {description}
      </p>
      
      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="flex flex-col gap-2 mb-6">
          <p className="text-xs text-[#999999] uppercase tracking-wide font-medium">
            Vorschläge
          </p>
          <ul className="space-y-1 text-sm text-[#666666]">
            {suggestions.map((suggestion, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#009544]" />
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        {onResetFilters && (
          <button
            onClick={onResetFilters}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#009544] text-white text-sm font-medium transition-colors hover:bg-[#007538] active:scale-[0.98]"
          >
            <FilterX className="w-4 h-4" />
            Filter zurücksetzen
          </button>
        )}
        
        {onChangeLocation && (
          <button
            onClick={onChangeLocation}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#F0F0ED] text-[#1A1A1A] text-sm font-medium transition-colors hover:bg-[#E8E8E5] active:scale-[0.98]"
          >
            <MapPin className="w-4 h-4" />
            Standort ändern
          </button>
        )}
      </div>
    </div>
  );
});

// ============================================
// ERROR STATE
// ============================================

interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
}

export const ErrorState = memo(function ErrorState({
  title = "Fehler beim Laden",
  description = "Die Restaurants konnten nicht geladen werden. Bitte versuche es erneut.",
  onRetry
}: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      {/* Icon */}
      <div className="w-20 h-20 rounded-2xl bg-rose-50 flex items-center justify-center mb-6">
        <RefreshCw className="w-10 h-10 text-rose-400" />
      </div>
      
      {/* Title */}
      <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">
        {title}
      </h3>
      
      {/* Description */}
      <p className="text-[#666666] text-sm max-w-sm mb-6">
        {description}
      </p>
      
      {/* Retry Button */}
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#009544] text-white text-sm font-medium transition-colors hover:bg-[#007538] active:scale-[0.98]"
        >
          <RefreshCw className="w-4 h-4" />
          Erneut versuchen
        </button>
      )}
    </div>
  );
});

// ============================================
// SWR INDICATOR (Background refetch)
// ============================================

export const RefreshingIndicator = memo(function RefreshingIndicator() {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs text-[#666666] shadow-sm border border-black/[0.06]">
      <span className="w-2 h-2 rounded-full bg-[#009544] animate-pulse" />
      Aktualisiere Ergebnisse…
    </div>
  );
});

// ============================================
// LOAD MORE INDICATOR
// ============================================

interface LoadMoreIndicatorProps {
  isLoading?: boolean;
  totalCount?: number;
  hasMore?: boolean;
}

export const LoadMoreIndicator = memo(function LoadMoreIndicator({
  isLoading = false,
  totalCount,
  hasMore = false
}: LoadMoreIndicatorProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <div className="flex items-center gap-2 text-sm text-[#666666]">
          <div 
            className="w-4 h-4 border-2 border-[#E8E8E5] rounded-full animate-spin" 
            style={{ borderTopColor: '#009544' }} 
          />
          <span>Lade weitere Restaurants...</span>
        </div>
      </div>
    );
  }
  
  if (!hasMore && totalCount) {
    return (
      <div className="flex justify-center py-4 text-sm text-[#999999]">
        Alle {totalCount} Restaurants angezeigt
      </div>
    );
  }
  
  return null;
});
